import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiRefreshCw, FiCalendar, FiAward, FiX } from 'react-icons/fi';
import CategoryService from '../services/CategoryService';

const renderCategoryIcon = (iconStr, defaultEmoji) => {
  if (!iconStr) return <span className="text-2xl">{defaultEmoji}</span>;
  if (typeof iconStr === 'string' && (iconStr.startsWith('http://') || iconStr.startsWith('https://') || iconStr.includes('/') || iconStr.includes('.webp') || iconStr.includes('.png') || iconStr.includes('.jpg') || iconStr.includes('.svg'))) {
    return <img src={iconStr} alt="" className="w-8 h-8 object-contain rounded-lg shrink-0" />;
  }
  return <span className="text-2xl shrink-0">{iconStr}</span>;
};

const Categories = () => {
  const [contestCategories, setContestCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [modal, setModal] = useState(null); // null | 'add' | { edit: item }
  const [form, setForm] = useState({ name: '', slug: '', description: '', icon: '' });

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const resContest = await CategoryService.getAllContestCategories().catch(() => ({ types: [] }));
      const cList = Array.isArray(resContest) ? resContest : (resContest.types || resContest.categories || resContest.data || []);
      setContestCategories(cList);
    } catch (err) {
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const currentList = contestCategories;

  const openAdd = () => {
    setForm({ name: '', slug: '', description: '', icon: '🏆' });
    setModal('add');
  };

  const openEdit = (cat) => {
    setForm({
      name: typeof cat.name === 'string' ? cat.name : (cat.name?.en || cat.name?.first || ''),
      slug: cat.slug || '',
      description: typeof cat.description === 'string' ? cat.description : (cat.description?.en || cat.shortDescription || ''),
      icon: cat.icon || '🏆',
    });
    setModal({ edit: cat });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { alert('Category name is required'); return; }
    const slug = form.slug || form.name.toLowerCase().replace(/ /g, '-').replace(/[^\w-]/g, '');
    const payload = {
      name: form.name,
      slug,
      description: form.description,
      shortDescription: form.description,
      icon: form.icon,
    };

    try {
      if (modal === 'add') {
        await CategoryService.addContestCategory(payload);
      } else if (modal?.edit) {
        await CategoryService.updateContestCategory(modal.edit._id, payload);
      }
      setModal(null);
      loadCategories();
    } catch (err) {
      alert('Failed to save category: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contest category?')) return;
    try {
      await CategoryService.deleteContestCategory(id);
      loadCategories();
    } catch (err) {
      alert('Failed to delete category');
    }
  };

  const handleToggle = async (cat) => {
    // Only applies if status toggling is supported for contest categories
  };

  return (
    <div className="space-y-6 max-w-6xl text-left">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white border border-slate-200 p-6 rounded-2xl shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">Categories Management</h1>
          <p className="text-xs font-medium text-slate-500 mt-1">Manage Contest categories</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadCategories} className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200 transition shadow-sm" title="Refresh">
            <FiRefreshCw className="w-4 h-4" />
          </button>
          <button onClick={openAdd} className="px-4 py-2.5 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition flex items-center gap-1.5 shadow-sm">
            <FiPlus className="w-4 h-4" /> Add Contest Category
          </button>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-20 bg-white border border-slate-200 rounded-2xl shadow-sm">
            <div className="w-7 h-7 border-2 border-purple-600/30 border-t-purple-600 rounded-full animate-spin" />
          </div>
        ) : currentList.length === 0 ? (
          <div className="col-span-full text-center py-20 bg-white border border-slate-200 rounded-2xl shadow-sm">
            <p className="text-sm font-bold text-slate-500">No contest categories found</p>
          </div>
        ) : currentList.map((cat) => {
          const name = typeof cat.name === 'string' ? cat.name : (cat.name?.en || cat.name?.first || 'Category');
          const desc = typeof cat.description === 'string' ? cat.description : (cat.description?.en || cat.shortDescription || '');
          const isHidden = cat.status === 'hide' || cat.isHidden === 'hide';

          return (
            <div key={cat._id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col justify-between gap-3 shadow-sm hover:border-slate-300 transition">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {renderCategoryIcon(cat.icon, '🏆')}
                  <div className="min-w-0 text-left">
                    <h3 className="text-sm font-bold text-slate-900 truncate">{name}</h3>
                    <p className="text-[10px] text-slate-500 font-medium truncate">{cat.slug}</p>
                  </div>
                </div>
              </div>

              {desc && <p className="text-[11px] text-slate-600 font-medium line-clamp-2 text-left">{desc}</p>}

              <div className="flex items-center gap-1.5 mt-auto pt-2 border-t border-slate-100">
                <button onClick={() => openEdit(cat)} className="p-1.5 rounded-lg text-slate-500 hover:text-purple-700 hover:bg-purple-50 transition" title="Edit">
                  <FiEdit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => handleDelete(cat._id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition" title="Delete">
                  <FiTrash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 w-full max-w-md space-y-4 text-left shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-sm font-bold text-slate-900">
                {modal === 'add' ? 'Add Contest Category' : 'Edit Contest Category'}
              </h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-600"><FiX className="w-4 h-4" /></button>
            </div>

            <div className="space-y-3">
              <Field label="Category Name *" value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))} placeholder="e.g. Photography & Arts" />
              <Field label="Slug" value={form.slug} onChange={v => setForm(p => ({ ...p, slug: v }))} placeholder="auto-generated" />
              <Field label="Icon (Emoji / Image URL)" value={form.icon} onChange={v => setForm(p => ({ ...p, icon: v }))} placeholder="🏆" />
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Short category description..."
                  className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 h-20 resize-none font-medium"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
              <button onClick={() => setModal(null)} className="px-4 py-2 rounded-xl bg-slate-100 text-slate-600 text-xs font-bold hover:bg-slate-200">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold hover:bg-purple-800 transition">Save Category</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">{label}</label>
    <input
      type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium transition"
    />
  </div>
);

export default Categories;
